import { fabric } from 'fabric';
import _ from 'lodash';

import { removeDuplicateElement } from '@/shared/ui/components/FabricJS/Helper';

type GridCtor = new (canvas: fabric.Canvas, config: any) => any;

let GridImpl: any;

(function () {
  GridImpl = function (canvas: fabric.Canvas, config: any) {
    this.canvas = canvas;
    // `getContext` isn't declared on @types/fabric's Canvas but fabric exposes the lower
    // canvas's 2d context at runtime.
    this.ctxChart = (this.canvas as any).getContext('2d');
    this.update(config);
    this.config = config;
    this.gridMajorColor = '#0954BA';
    this.gridMinorColor = '#CEE2FE';
    this.configObject = {
      selectable: false,
      hasControls: false,
      hasBorders: false,
      hasRotatingPoint: false,
      selection: false,
      skipTargetFind: false,
      editable: false,
    };
    this.gridsLines = [];
    this.sizeGrid = 0;
  };

  GridImpl.prototype.draw = function () {
    // *: Clear canvas to draw a new grid
    this.canvas.clear();
    // *: Draw square minor
    this.drawSquareGrid(this.majorDivSize / 5, 1);

    // *: Draw border
    this.drawBorder();
    if (this.fineGrid) {
      this.drawSquareGrid(this.majorDivSize * 0.04, 1); // 0.04sec
    }
  };

  GridImpl.prototype.drawExportGrid = function () {
    this.sizeGrid = 0;

    // *: Draw square minor
    this.drawExportSquareGrid(this.canvas, this.majorDivSize / 5, 1, this.gridMinorColor);

    // *: Draw square major
    this.drawExportBorder();

    if (this.fineGrid) {
      this.drawExportSquareGrid(this.canvas, this.majorDivSize * 0.04, 1, this.gridMinorColor);
    }
    const gridsLinesLength = this.gridsLines.length;
    for (let i = this.sizeGrid; i < gridsLinesLength; i += 1) {
      this.gridsLines[i].visible = false;
    }
  };

  GridImpl.prototype.render = function () {
    this.canvas.renderAll();
  };

  GridImpl.prototype.update = function (config: any) {
    this.config = config;
    const { width, height, majorDivSize, fineGrid } = config;
    this.width = width;
    this.height = height;
    this.majorDivSize = majorDivSize;
    this.fineGrid = fineGrid;
  };

  GridImpl.prototype.drawSquareGrid = function (side: number, thickness: number) {
    this.ctxChart.strokeStyle = this.gridMinorColor;
    this.ctxChart.lineWidth = thickness;

    // *: Draw vertical lines
    let i = 0;
    for (i = 0; i < this.width; i += side) {
      this.drawLine(i, this.height, i, 0);
    }
    if (i <= this.width) {
      this.drawLine(this.width, this.height, this.width, 0);
    }

    // *: Draw horizontal lines
    for (i = 0; i < this.height; i += side) {
      this.drawLine(this.width, i, 0, i);
    }
    if (i <= this.height) {
      this.drawLine(this.width, this.height, 0, this.height);
    }
  };

  GridImpl.prototype.drawLine = function (x1: number, y1: number, x2: number, y2: number) {
    this.ctxChart.beginPath();
    this.ctxChart.moveTo(x1, y1);
    this.ctxChart.lineTo(x2, y2);
    this.ctxChart.stroke();
    this.ctxChart.closePath();
  };

  GridImpl.prototype.drawBorder = function () {
    this.ctxChart.strokeStyle = this.gridMajorColor;
    this.ctxChart.lineWidth = 3;
    this.drawLine(0, 0, this.width, 0);
    this.drawLine(0, 0, 0, this.height);
    this.drawLine(this.width, 0, this.width, this.height);
    this.drawLine(0, this.height, this.width, this.height);
  };

  GridImpl.prototype.drawExportLine = function (
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    strokeWidth: number,
    stroke: string,
  ) {
    if (this.sizeGrid < this.gridsLines.length) {
      this.gridsLines[this.sizeGrid].set({
        x1,
        y1,
        x2,
        y2,
        strokeWidth,
        stroke,
      });
      this.gridsLines[this.sizeGrid].setCoords();
      this.gridsLines[this.sizeGrid].visible = true;
    } else {
      const line = new fabric.Line([x1, y1, x2, y2], {
        ...this.configObject,
        strokeWidth,
        stroke,
      });
      this.canvas.add(line);
      this.gridsLines.push(line);
    }
    this.sizeGrid += 1;
  };

  GridImpl.prototype.drawExportSquareGrid = function (
    context: any,
    side: number,
    thickness: number,
    color: string,
  ) {
    context.strokeStyle = color;

    // *: Draw vertical lines
    let i = 0;
    for (i = 0; i < this.width; i += side) {
      this.drawExportLine(i - thickness / 2, this.height, i - thickness / 2, 0, thickness, color);
    }
    if (i <= this.width + thickness) {
      this.drawExportLine(
        this.width - thickness / 2,
        this.height,
        this.width - thickness / 2,
        0,
        thickness,
        color,
      );
    }

    // *: Draw horizontal lines
    for (i = 0; i < this.height; i += side) {
      this.drawExportLine(this.width, i - thickness / 2, 0, i - thickness / 2, thickness, color);
    }
    if (i <= this.height + thickness) {
      this.drawExportLine(
        this.width,
        this.height - thickness / 2,
        0,
        this.height - thickness / 2,
        thickness,
        color,
      );
    }
  };

  GridImpl.prototype.drawExportBorder = function () {
    this.drawExportLine(0, 0, this.width, 0, 1, this.gridMajorColor);
    this.drawExportLine(this.width - 1, 0, this.width - 1, this.height, 1, this.gridMajorColor); // *: Minus 0.5px
    this.drawExportLine(
      0,
      this.height - 1,
      this.width - 0.5,
      this.height - 1,
      1,
      this.gridMajorColor,
    ); // *: Minus 0.5px
    this.drawExportLine(0, 0, 0, this.height, 1, this.gridMajorColor);
  };

  GridImpl.prototype.showHideGrid = function (isVisibility: boolean) {
    _.forEach(this.gridsLines, (x: fabric.Line) => {
      x.visible = isVisibility;
    });
  };

  GridImpl.prototype.export = function () {
    const svg = this.canvas.toSVG(
      {
        viewBox: {
          x: 0,
          y: 0,
          width: 945,
          height: 250,
        },
        suppressPreamble: true,
      },
      (e: string) => (e.includes('hidden') ? '' : e),
    );

    return removeDuplicateElement(svg);
  };
})();

const Grid: GridCtor = GridImpl;

export { Grid };
export default { Grid };
